import React from "react";
import Link from "next/link";
import Icon from "@mdi/react";
import { mdiPlay } from "@mdi/js";
import { BetQLSportItem } from "src/common-services";

import TeamLogo from "../../../Team/TeamLogo";
import { COMP_GREEN } from "../../../../colors";
import { TrackingProperties } from "../../../../constants";
import LinkWithHandler from "../../../LinkWithHandler";
import AnalyticsService from "../../../../services/AnalyticsService";
import { useGameProgressAndChannel } from "src/services/EventService";

type PickType = "home" | "away" | "draw";

interface TeamCellProps {
  game: any;
  periodFilter: string;
  pageKey: string;
  currentSportObj: BetQLSportItem;
  trackingProps: TrackingProperties;
  trend: any;
  ourPick: PickType;
  showDraw: boolean;
  hasAccess: boolean;
}

/**
 * Track that the user clicked on this particular game.
 *
 * @param game the game to track being selected
 * @param trackingProps the properties to send along with the tracking event
 */
const _trackGameSelected = async (game: any, trackingProps: TrackingProperties) => {
  if (trackingProps) {
    AnalyticsService.track("Open Game", {
      screen: "FeedView",
      awayTeam: game.awayTeam.fullName,
      homeTeam: game.homeTeam.fullName,
      gameId: game.id,
      ...trackingProps,
    });
  }
};

export const TeamCell = ({
  game,
  periodFilter,
  pageKey,
  currentSportObj,
  trackingProps,
  trend,
  ourPick,
  showDraw,
  hasAccess,
}: TeamCellProps) => {
  const isTotalBet = periodFilter.includes("total");
  const { favoredTeam } = game.trendsPick;

  const showOurPick = (pick: PickType) => {
    // only bold if it's freeGame or hasSubscription and not movement
    const noAwayLine = game[`current${periodFilter}AwayDisplay`] === "--";
    const noHomeLine = game[`current${periodFilter}HomeDisplay`] === "--";
    const hasCurrentLine = !noAwayLine && !noHomeLine;

    if (hasAccess && pageKey !== "movement" && hasCurrentLine) {
      // show arrow if we have data
      switch (pageKey) {
        case "sharp":
          const noSharpPercentage = game[`sharp${periodFilter}Display`] === "--";

          return !noSharpPercentage && ourPick === pick;
        case "public":
          const noPublicPercentage = game[`sharp${periodFilter}TicketDisplay`] === "--";

          return !noPublicPercentage && ourPick === pick;
        case "value":
          const preferredTotal = game[`value${periodFilter}PreferredTotal`];
          const isPush = isTotalBet && preferredTotal && preferredTotal === "push";

          return trend && !!trend.displayRating && !isPush && ourPick === pick;
        case "trend":
          return !isTotalBet && favoredTeam === pick;
      }
    }
    return false;
  };

  const getBoldTeamClassName = (pick: PickType) => (showOurPick(pick) ? "games-table-column__team-name--bold" : "");

  const awayRank = game?.awayTeamSeed ?? game?.awayTeam?.teamStats?.rank ?? "";
  const homeRank = game?.homeTeamSeed ?? game?.homeTeam?.teamStats?.rank ?? "";

  const progressAndChannel = useGameProgressAndChannel(game);

  return (
    <Link href={`/${currentSportObj.url}/game-predictions/${game.slugId}`} passHref legacyBehavior>
      <LinkWithHandler
        className="games-table-column__team-link"
        onCustomClick={() => _trackGameSelected(game, trackingProps)}
      >
        <div className={`games-table-column__team-cell ${showDraw ? "games-table-column__team-cell--draw" : ""}`}>
          <div className="games-table-column__team-date-cell">
            {progressAndChannel}
            <span className="games-table-column__team-date-cell--live">
              {game.eventState === "STARTED" ? "Live" : ""}
            </span>
          </div>
          <div className="games-table-column__team-container">
            <div className="games-table-column__team-image-info-container">
              <TeamLogo
                containerClassName="games-table-column__team-image-container"
                leagueName={currentSportObj.imagePath}
                teamAbbreviation={
                  currentSportObj.isTennis ? game.awayTeam.country : game.awayTeam.preferredAbbreviation
                }
                size={25}
              />
              <div className="games-table-column__team-info-container">
                <p className={`games-table-column__team-name ${getBoldTeamClassName("away")}`}>
                  {currentSportObj.isCollege ? game.awayTeam.fullName : game.awayTeam.lastName}
                  {awayRank ? (
                    <span className="games-table-column__rank-container">
                      <span className="games-table-column__rank-text">{awayRank}</span>
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="games-table-column__green-arrow">
              {showOurPick("away") ? <Icon path={mdiPlay} size="22px" color={COMP_GREEN} /> : null}
            </div>
          </div>
          <div className="games-table-column__team-container">
            <div className="games-table-column__team-image-info-container">
              <TeamLogo
                containerClassName="games-table-column__team-image-container"
                leagueName={currentSportObj.imagePath}
                teamAbbreviation={
                  currentSportObj.isTennis ? game.homeTeam.country : game.homeTeam.preferredAbbreviation
                }
                size={25}
              />
              <div className="games-table-column__team-info-container">
                <p className={`games-table-column__team-name ${getBoldTeamClassName("home")}`}>
                  {currentSportObj.isCollege ? game.homeTeam.fullName : game.homeTeam.lastName}
                  {homeRank ? (
                    <span className="games-table-column__rank-container">
                      <span className="games-table-column__rank-text">{homeRank}</span>
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="games-table-column__green-arrow">
              {showOurPick("home") ? <Icon path={mdiPlay} size="22px" color={COMP_GREEN} /> : null}
            </div>
          </div>
          {showDraw ? (
            <div className="games-table-column__team-container">
              <div className="games-table-column__team-image-info-container">
                <div className="games-table-column__team-image-container" style={{ width: "25px" }} />
                <div className="games-table-column__team-info-container">
                  <p className={`games-table-column__team-name ${getBoldTeamClassName("draw")}`}>Draw</p>
                </div>
              </div>
              <div className="games-table-column__green-arrow">
                {showOurPick("draw") ? <Icon path={mdiPlay} size="22px" color={COMP_GREEN} /> : null}
              </div>
            </div>
          ) : null}
          <div className="games-table-column__game-detail-link">See Full Analysis</div>
        </div>
      </LinkWithHandler>
    </Link>
  );
};
